// A two-month range calendar for list toolbars, with the basis it filters on
// named inside the control (job 75, "Add Dates Filter").
//
// ── The string boundary ─────────────────────────────────────────────────────
// Props are `YYYY-MM-DD` strings, not Dates, deliberately. The predicate that
// consumes them (utils/billDateFilter) compares Postgres DATE columns as plain
// strings — no parsing, no timezone — and is covered by tests on that basis.
// Date objects live only inside this component, where the calendar needs them.
//
// Both conversions are timezone-safe on purpose:
//   parseISO('2026-06-01')  → LOCAL midnight. `new Date('2026-06-01')` would be
//                             UTC midnight, which is the previous day anywhere
//                             behind UTC — the picker would open on 31 May.
//   format(d,'yyyy-MM-dd')  → LOCAL calendar date. `toISOString().slice(0,10)`
//                             would be the UTC date, so clicking 1 June in
//                             Melbourne would emit 2026-05-31.

import { useState } from 'react';
import { CalendarDays, X } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import {
  addMonths, format, isValid, parseISO,
  startOfMonth, endOfMonth, subMonths,
} from 'date-fns';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { Calendar } from '../../../../foundation/altzero_base/base/components/ui/calendar';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../../../foundation/altzero_base/base/components/ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../foundation/altzero_base/base/components/ui/select';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { DATE_BASIS_LABEL, type DateBasis } from '../../utils/billDateFilter';

/** `YYYY-MM-DD` → Date at local midnight, or undefined if absent/unparseable. */
const toDate = (key: string): Date | undefined => {
  if (!key) return undefined;
  const d = parseISO(key);
  return isValid(d) ? d : undefined;
};

/** Date → `YYYY-MM-DD` using its LOCAL calendar date. */
const toKey = (d: Date): string => format(d, 'yyyy-MM-dd');

const fmtLabel = (key: string): string => {
  const d = toDate(key);
  return d ? format(d, 'd MMM yyyy') : '';
};

/**
 * The Australian financial year starts 1 July — this is an energy retail tool,
 * so "FY to date" means the AU one, not the calendar year.
 */
function auFinancialYearStart(now: Date): Date {
  const july = 6; // zero-based
  const year = now.getMonth() >= july ? now.getFullYear() : now.getFullYear() - 1;
  return new Date(year, july, 1);
}

type Preset = { label: string; range: (now: Date) => [Date, Date] };

const PRESETS: Preset[] = [
  { label: 'This month',     range: (n) => [startOfMonth(n), endOfMonth(n)] },
  { label: 'Last month',     range: (n) => [startOfMonth(subMonths(n, 1)), endOfMonth(subMonths(n, 1))] },
  { label: 'Last 3 months',  range: (n) => [startOfMonth(subMonths(n, 2)), endOfMonth(n)] },
  { label: 'FY to date',     range: (n) => [auFinancialYearStart(n), n] },
];

type DateRangeFilterProps = {
  basis: DateBasis;
  /**
   * Omit where the list has only one date to filter on. The basis then renders
   * as a static label instead of a select — a dropdown whose other option does
   * nothing is worse than no dropdown.
   */
  onBasisChange?: (basis: DateBasis) => void;
  /** Overrides the basis name, for a list whose date is called something else. */
  basisLabel?: string;
  /** `YYYY-MM-DD`, or '' for an open end. */
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
};

export function DateRangeFilter({
  basis, onBasisChange, basisLabel, from, to, onChange,
}: DateRangeFilterProps) {
  const [open, setOpen] = useState(false);

  const selected: DateRange | undefined = from || to
    ? { from: toDate(from), to: toDate(to) }
    : undefined;

  const active = !!(from || to);

  // A half-open range is legitimate and must stay sayable, so this reads each
  // end independently rather than requiring both.
  const triggerLabel = !active
    ? 'All dates'
    : from && to
      ? `${fmtLabel(from)} – ${fmtLabel(to)}`
      : from
        ? `From ${fmtLabel(from)}`
        : `Until ${fmtLabel(to)}`;

  const applyPreset = (preset: Preset) => {
    const [start, end] = preset.range(new Date());
    onChange(toKey(start), toKey(end));
    setOpen(false);
  };

  return (
    <div className={cn(
      'flex items-center gap-1 h-9 rounded-md border pl-2 pr-1',
      active ? 'border-blue-400 bg-blue-50/40' : 'border-input',
    )}>
      <CalendarDays className={cn('h-3.5 w-3.5 shrink-0', active ? 'text-blue-600' : 'text-muted-foreground')} />

      {/* Which of the list's dates is being filtered. Named in the control
          because the Bills table shows two and they answer different questions.
          Where a list has only one, it is stated rather than offered. */}
      {onBasisChange ? (
        <Select value={basis} onValueChange={(v) => onBasisChange(v as DateBasis)}>
          <SelectTrigger
            className="h-7 w-[6.5rem] border-0 shadow-none px-1 text-xs focus:ring-0"
            aria-label="Which date to filter on"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(DATE_BASIS_LABEL) as DateBasis[]).map((b) => (
              <SelectItem key={b} value={b}>{DATE_BASIS_LABEL[b]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span className="px-1 text-xs text-muted-foreground whitespace-nowrap">
          {basisLabel ?? DATE_BASIS_LABEL[basis]}
        </span>
      )}

      <span className="text-muted-foreground/40">|</span>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'h-7 px-2 text-xs font-normal justify-start',
              active ? 'text-blue-700' : 'text-muted-foreground',
            )}
          >
            {triggerLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            numberOfMonths={2}
            // Monday-first, matching en-AU.
            weekStartsOn={1}
            // Open on the selected range, or on the two months ending with this
            // one — bills are historic, so the useful months are behind us.
            defaultMonth={toDate(from) ?? addMonths(new Date(), -1)}
            selected={selected}
            onSelect={(range) => {
              // Clicking a selected single day clears the range in RDP, which
              // arrives as undefined — that means "no filter", not "keep".
              onChange(
                range?.from ? toKey(range.from) : '',
                range?.to ? toKey(range.to) : '',
              );
            }}
          />
          <div className="flex flex-wrap gap-1 border-t p-2">
            {PRESETS.map((p) => (
              <Button
                key={p.label}
                variant="outline"
                size="sm"
                className="h-7 text-xs font-normal"
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </Button>
            ))}
            {active && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs font-normal text-muted-foreground ml-auto"
                onClick={() => { onChange('', ''); setOpen(false); }}
              >
                Clear
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {active && (
        <button
          onClick={() => onChange('', '')}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-gray-700 hover:bg-gray-100"
          title="Clear date range"
          aria-label="Clear date range"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
